'use client';

import * as React from 'react';
import {
  ClipboardList,
  Zap,
  CheckCircle2,
  FileText,
  Upload,
  LayoutDashboard,
  Loader2,
  Trophy,
  Target,
} from 'lucide-react';

import { Card, CardContent, CardHeader } from '@/components/ui/card';
// import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

import { useTenant } from '@/hooks';
import {
  useGetMilestoneDeliverablesQuery,
  useDeleteMilestoneDeliverableMutation,
  useAddDeliverableMutation,
  useGetExternalMilestoneDeliverablesQuery,
  useAddExternalDeliverableMutation,
  useDeleteExternalDeliverableMutation,
} from '@/state';

import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { cn } from '@/lib/utils';

import type { MilestoneOverviewProps, MilestoneDeliverable } from '@/types';
import { DeliverableItem } from './deliverable-item';
import UnderlinedTabs from '@/components/tabs';
import { AxiosError } from 'axios';

export function MilestoneOverview({
  description,
  counts,
  milestoneId,
  isExternal,
  isReadOnly = false,
}: MilestoneOverviewProps) {
  const { tenant } = useTenant();

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = React.useState('overview');
  const [isUploading, setIsUploading] = React.useState(false);
  const [expandedDoc, setExpandedDoc] = React.useState<number | null>(null);

  // API (Internal)
  const {
    data: internalDeliverables = [],
    isLoading: isLoadingInternalDocs,
    refetch: refetchInternalDocs,
  } = useGetMilestoneDeliverablesQuery(
    { tenant: tenant || '', milestoneId },
    { skip: !tenant || !milestoneId || isExternal }
  );
  const [addInternalDeliverable] = useAddDeliverableMutation();
  const [deleteInternalDeliverable] = useDeleteMilestoneDeliverableMutation();

  // API (External)
  const {
    data: externalDeliverables = [],
    isLoading: isLoadingExternalDocs,
    refetch: refetchExternalDocs,
  } = useGetExternalMilestoneDeliverablesQuery(
    { milestoneId },
    { skip: !milestoneId || !isExternal }
  );
  const [addExternalDeliverable] = useAddExternalDeliverableMutation();
  const [deleteExternalDeliverable] = useDeleteExternalDeliverableMutation();

  const deliverables = isExternal ? externalDeliverables : internalDeliverables;
  const isLoadingDocs = isExternal ? isLoadingExternalDocs : isLoadingInternalDocs;
  const refetchDocs = isExternal ? refetchExternalDocs : refetchInternalDocs;
  const deliverablesList: MilestoneDeliverable[] = React.useMemo(() => {
    const raw = Array.isArray(deliverables)
      ? deliverables
      : (deliverables as { results?: MilestoneDeliverable[] } | undefined)
          ?.results;

    return Array.isArray(raw) ? (raw as MilestoneDeliverable[]) : [];
  }, [deliverables]);

  // Upload Handler
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || (!isExternal && !tenant) || !milestoneId) return;

    try {
      setIsUploading(true);

      const formData = new FormData();
      formData.append('attachment', file);
      formData.append('title', file.name);
      formData.append('description', 'Milestone Deliverable');
      formData.append('milestone', String(milestoneId));

      if (isExternal) {
        await addExternalDeliverable({ data: formData }).unwrap();
      } else {
        await addInternalDeliverable({ tenant: tenant!, data: formData }).unwrap();
      }

      toast.success('Document uploaded successfully');
      refetchDocs();
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        toast.error(
          err.response?.data?.message ?? 'Failed to upload document'
        );
      } else {
        toast.error('Failed to upload document');
      }
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Delete Handler
  const handleDeleteDoc = async (id: number) => {
    if (!isExternal && !tenant) return;

    try {
      if (isExternal) {
        await deleteExternalDeliverable({
          deliverableId: id,
          milestoneId,
        }).unwrap();
      } else {
        await deleteInternalDeliverable({
          tenant: tenant!,
          deliverableId: id,
        }).unwrap();
      }
      toast.success('Deliverable removed');
      refetchDocs();
    } catch {
      toast.error('Failed to remove deliverable');
    }
  };

  return (
    <Card className="bg-transparent border-none m-0 p-0">
      <CardHeader />
      <CardContent>
        <UnderlinedTabs
          value={activeTab}
          onValueChange={setActiveTab}
          // makeSticky
          tabItems={[
            // =========================
            // OVERVIEW TAB
            // =========================
            {
              value: 'overview',
              title: 'Strategy & Insights',
              icon: <LayoutDashboard className="w-3.5 h-3.5" />,
              content: (
                <div className="space-y-10 animate-in fade-in slide-in-from-bottom-2 duration-500">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                      {
                        label: 'Assigned',
                        value: counts.todo,
                        color: 'slate',
                        icon: ClipboardList,
                        bg: 'hover:bg-slate-500/5',
                      },
                      {
                        label: 'In Action',
                        value: counts.doing,
                        color: 'sky',
                        icon: Zap,
                        bg: 'hover:bg-sky-500/5',
                      },
                      {
                        label: 'Resolved',
                        value: counts.done,
                        color: 'emerald',
                        icon: CheckCircle2,
                        bg: 'hover:bg-emerald-500/5',
                      },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className={cn(
                          'group relative overflow-hidden rounded-2xl border border-border/40 p-6 transition-all duration-300 hover:shadow-lg bg-background/40',
                          item.bg
                        )}
                      >
                        <div className="flex items-center gap-5">
                          <div
                            className={cn(
                              'flex items-center justify-center w-12 h-12 rounded-xl transition-transform duration-300 group-hover:scale-105',
                              item.color === 'slate'
                                ? 'bg-slate-500/5 text-slate-500'
                                : item.color === 'sky'
                                  ? 'bg-sky-500/5 text-sky-500'
                                  : 'bg-emerald-500/5 text-emerald-500'
                            )}
                          >
                            <item.icon className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="text-[9px] font-semibold text-muted-foreground/50 uppercase tracking-tight">
                              {item.label}
                            </div>
                            <div className="text-xl font-semibold text-foreground/80">
                              {item.value}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Description */}
                  {description && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Target className="w-4 h-4 text-primary opacity-50" />
                        <div className="text-[10px] font-semibold text-foreground/50 uppercase tracking-wider">
                          Phase Context
                        </div>
                      </div>

                      <div className="rounded-3xl border border-border/20 p-8 bg-muted/10 backdrop-blur-sm relative overflow-hidden group shadow-sm">
                        <div className="absolute -top-6 -right-6 opacity-[0.02] pointer-events-none">
                          <Trophy className="w-48 h-48" />
                        </div>

                        <ReadOnlyContentDisplay
                          html={description}
                          className="text-base text-foreground/80 relative z-10 font-normal leading-relaxed"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ),
            },

            // =========================
            // DELIVERABLES TAB
            // =========================
            {
              value: 'deliverables',
              title: 'Document Repository',
              icon: (
                <div className="flex items-center gap-2">
                  <FileText className="w-3.5 h-3.5" />
                  {deliverablesList.length > 0 && (
                    <Badge className="rounded-full h-4 min-w-[1rem] p-0 px-1 text-[9px]">
                      {deliverablesList.length}
                    </Badge>
                  )}
                </div>
              ),
              content: (
                <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
                  {/* Upload Section */}
                  {!isReadOnly && (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between bg-muted/10 p-6 md:p-8 rounded-3xl border border-border/40 shadow-sm gap-6">
                      <div className="space-y-1">
                        <h3 className="text-xl font-bold tracking-tight text-foreground/90">
                          Asset Repository
                        </h3>
                        <p className="text-xs text-muted-foreground font-medium">
                          Centralized management for milestone deliverables.
                        </p>
                      </div>

                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading}
                        
                      >
                        {isUploading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        Upload Asset
                      </Button>

                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        onChange={handleFileUpload}
                      />
                    </div>
                  )}

                  {/* Deliverables List */}
                  {isLoadingDocs ? (
                    <div className="flex flex-col items-center justify-center py-20 opacity-60">
                      <Loader2 className="w-10 h-10 animate-spin text-primary mb-4" />
                      <p className="text-xs font-semibold text-muted-foreground">
                        Synchronizing Assets...
                      </p>
                    </div>
                  ) : deliverablesList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 rounded-3xl border border-dashed border-border/40 bg-muted/5">
                      <Upload className="w-8 h-8 text-muted-foreground mb-4" />
                      <p className="text-lg font-bold text-foreground/70">
                        No Assets Recorded
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Upload the primary deliverables for this milestone.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-6">
                      {deliverablesList.map((doc) => (
                        <DeliverableItem
                          key={doc.id}
                          doc={doc}
                          tenant={tenant!}
                          isExpanded={expandedDoc === doc.id}
                          isExternal={isExternal}
                          isReadOnly={isReadOnly}
                          onToggle={() =>
                            setExpandedDoc(
                              expandedDoc === doc.id ? null : doc.id
                            )
                          }
                          onDelete={() => handleDeleteDoc(doc.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </CardContent>
    </Card>
  );
}
